import { useQueryClient } from '@tanstack/react-query'
import {
  ActiveShoppingCartQuery,
  useActiveShoppingCartQuery,
  useAddEbookBundleToShoppingCartMutation,
  useAddItemToShoppingCartMutation,
  useCreateAnonymousShoppingCartMutation,
  useCreateCustomerShoppingCartMutation,
} from '@/generated/graphql'
import { useStore } from '@/store'
import { EBOOK_BUNDLE_SENTINEL } from '@/ebookBundle'
import { isConventionRegistrant } from '@/conventions/cartMetadata'
import {
  isAuthenticationFailure,
  isAuthorizationFailure,
  type GraphQLErrorLike,
} from '@/errorHandling'
import { ORIGINAL_CART_SNAPSHOT_KEY } from '@/Conventions/useConventionRegistration'
import { useStorefrontMarket } from '@/useStorefrontMarket'
import { assertCartMutationSucceeded } from '@/lib/cartMutationResult'

export type AddItemToCartInput = {
  /** Optional explicit cart token override (use when caller already has the target cart). */
  shoppingCartToken?: string | null
  productId: number
  productOptionId?: number | null
  description?: string | null
  quantity?: number
  /**
   * Misc. product (8640) only: gross line amount. UK (GBP) carts: API stores ex-VAT (÷ 1.2).
   */
  unitPrice?: number | null
  /** When productOptionId is EBOOK_BUNDLE_SENTINEL, required for AddEbookBundleToShoppingCart */
  ebookBundleOption?: {
    physicalOptionId: number
    downloadOptionId: number
  } | null
}

type UseAddItemToCartArgs = {
  onSuccess?: (input: AddItemToCartInput) => void
  onError?: (error: unknown) => void
  onCartCreateError?: () => void
}

type ActiveCart = NonNullable<ActiveShoppingCartQuery['activeShoppingCart']>

const GUEST_CART_TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const INVALID_CART_IDENTIFIER_MESSAGE = 'No valid customer ID provided'
const CART_FETCH_STALE_TIME_MS = 30_000
const BROWSER_COOKIE_COMMIT_DELAY_MS = 150

const RECOVERABLE_CART_ADD_ERROR_MESSAGES = [
  INVALID_CART_IDENTIFIER_MESSAGE.toLowerCase(),
  'shopping cart not found',
  'you must be logged in to update this shopping cart',
  'you are not authorized to update this shopping cart',
  'your session is no longer valid',
  'your session has expired',
  'session expired',
  'cart session has expired',
]

const normalizeCartToken = (token: string | null | undefined) => {
  const normalizedToken = token?.trim()
  if (!normalizedToken || !GUEST_CART_TOKEN_PATTERN.test(normalizedToken)) {
    return null
  }

  return normalizedToken
}

const getNormalizedErrorText = (error: unknown): string => {
  const graphQLError = error as GraphQLErrorLike
  const graphQLErrorMessages =
    graphQLError.response?.errors?.map(e => e.message).filter(Boolean) ?? []

  return [graphQLError.message, ...graphQLErrorMessages]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const isRecoverableCartAddFailure = (error: unknown): boolean => {
  if (isAuthenticationFailure(error) || isAuthorizationFailure(error)) {
    return true
  }

  const message = getNormalizedErrorText(error)
  return RECOVERABLE_CART_ADD_ERROR_MESSAGES.some(fragment =>
    message.includes(fragment)
  )
}

const waitForBrowserCookieCommit = async () => {
  if (typeof window === 'undefined') return

  await new Promise(resolve =>
    window.setTimeout(resolve, BROWSER_COOKIE_COMMIT_DELAY_MS)
  )
}

/**
 * Core cart action hook.
 *
 * Owns:
 * - ensuring a cart exists (customer vs anonymous)
 * - adding an item via GraphQL mutation
 * - invalidating the ActiveShoppingCart query
 *
 * Does NOT own UI (no modals/toasts by default). Consumers can provide callbacks.
 */
export const useAddItemToCart = ({
  onSuccess,
  onError,
  onCartCreateError,
}: UseAddItemToCartArgs = {}) => {
  const queryClient = useQueryClient()
  const user = useStore(state => state.user)
  const shoppingCartId = useStore(state => state.shoppingCartId)
  const setShoppingCartId = useStore(state => state.setShoppingCartId)
  const storefrontMarket = useStorefrontMarket()

  const {
    mutateAsync: createAnonymousShoppingCart,
    isPending: isCreatingAnon,
  } = useCreateAnonymousShoppingCartMutation()
  const {
    mutateAsync: createCustomerShoppingCart,
    isPending: isCreatingCustomer,
  } = useCreateCustomerShoppingCartMutation()

  const {
    mutateAsync: addItemToShoppingCart,
    isPending: isAddingItem,
    isError,
  } = useAddItemToShoppingCartMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
    },
  })

  const {
    mutateAsync: addEbookBundleToShoppingCart,
    isPending: isAddingEbookBundle,
  } = useAddEbookBundleToShoppingCartMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
    },
  })

  const getCountryIsoCode = () => storefrontMarket.countryIsoCode

  const getActiveUserId = () => useStore.getState().user?.id ?? user?.id ?? null

  const getStoredCartToken = () =>
    normalizeCartToken(useStore.getState().shoppingCartId ?? shoppingCartId)

  const getActiveCartSnapshot = async (): Promise<ActiveCart | null> => {
    const activeUserId = getActiveUserId()

    if (activeUserId) {
      try {
        const variables = {
          customerId: activeUserId,
          includeConventionCart: false,
        }
        const result = await queryClient.fetchQuery({
          queryKey: useActiveShoppingCartQuery.getKey(variables),
          queryFn: () => useActiveShoppingCartQuery.fetcher(variables)(),
          staleTime: CART_FETCH_STALE_TIME_MS,
        })

        return result.activeShoppingCart ?? null
      } catch {
        return null
      }
    }

    const anonymousCartToken = getStoredCartToken()
    if (!anonymousCartToken) {
      return null
    }

    try {
      const variables = {
        anonymousCartToken,
        includeConventionCart: false,
      }
      const result = await queryClient.fetchQuery({
        queryKey: useActiveShoppingCartQuery.getKey(variables),
        queryFn: () => useActiveShoppingCartQuery.fetcher(variables)(),
        staleTime: CART_FETCH_STALE_TIME_MS,
      })

      return result.activeShoppingCart ?? null
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (message.includes(INVALID_CART_IDENTIFIER_MESSAGE)) {
        setShoppingCartId(null)
      }
      return null
    }
  }

  const createCartId = async (): Promise<string | null> => {
    const countryIsoCode = getCountryIsoCode()
    const activeUserId = getActiveUserId()

    if (activeUserId) {
      try {
        const result = await createCustomerShoppingCart({
          customerId: activeUserId,
          countryIsoCode,
        })

        const cartToken = result.createCustomerShoppingCart ?? null
        useStore.getState().setShoppingCartId(cartToken)

        if (!cartToken) onCartCreateError?.()
        if (cartToken) await waitForBrowserCookieCommit()
        return cartToken
      } catch (error) {
        if (!isAuthorizationFailure(error)) {
          throw error
        }
      }
    }

    const result = await createAnonymousShoppingCart({ countryIsoCode })
    const cartToken = result.createAnonymousShoppingCart ?? null
    useStore.getState().setShoppingCartId(cartToken)

    if (!cartToken) onCartCreateError?.()
    return cartToken
  }

  const ensureCartId = async (): Promise<string | null> => {
    const activeCart = await getActiveCartSnapshot()

    if (activeCart?.token) {
      if (
        activeCart.items?.some(item => isConventionRegistrant(item.metadata))
      ) {
        return switchToStandardCartFromConvention()
      }

      return activeCart.token
    }

    if (getActiveUserId()) {
      return createCartId()
    }

    const storedCartToken = getStoredCartToken()
    if (storedCartToken) return storedCartToken

    return createCartId()
  }

  const switchToStandardCartFromConvention = async (): Promise<
    string | null
  > => {
    const countryIsoCode = getCountryIsoCode()
    const activeUserId = getActiveUserId()

    if (activeUserId) {
      try {
        const result = await createCustomerShoppingCart({
          customerId: activeUserId,
          countryIsoCode,
        })
        const cartToken = result.createCustomerShoppingCart ?? null
        setShoppingCartId(cartToken)
        if (cartToken) await waitForBrowserCookieCommit()
        return cartToken
      } catch (error) {
        if (!isAuthorizationFailure(error)) {
          throw error
        }
      }
    }

    try {
      const raw = sessionStorage.getItem(ORIGINAL_CART_SNAPSHOT_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as { cartId?: string | null } | null
        if (parsed?.cartId && parsed.cartId.length > 0) {
          setShoppingCartId(parsed.cartId)
          return parsed.cartId
        }
      }
    } catch {
      // best-effort only
    }

    const result = await createAnonymousShoppingCart({ countryIsoCode })
    const cartToken = result.createAnonymousShoppingCart ?? null
    setShoppingCartId(cartToken)
    return cartToken
  }

  const addItem = async (
    input: AddItemToCartInput,
    opts?: { skipCallbacks?: boolean }
  ) => {
    try {
      const cartToken =
        input.shoppingCartToken && input.shoppingCartToken.length > 0
          ? input.shoppingCartToken
          : await ensureCartId()
      if (!cartToken) {
        if (!opts?.skipCallbacks) {
          return
        }
        throw new Error('Shopping cart unavailable')
      }

      const normalizedQuantity = Math.max(1, Math.floor(input.quantity ?? 1))
      const isEbookBundle =
        input.productOptionId === EBOOK_BUNDLE_SENTINEL &&
        input.ebookBundleOption
      const addToCart = async (shoppingCartToken: string) => {
        if (isEbookBundle) {
          const { physicalOptionId, downloadOptionId } =
            input.ebookBundleOption!
          const result = await addEbookBundleToShoppingCart({
            shoppingCartToken,
            productId: input.productId,
            physicalOptionId,
            downloadOptionId,
            quantity: normalizedQuantity,
          })
          assertCartMutationSucceeded(result.addEbookBundleToShoppingCart)
          return
        }

        const rawOptionId = input.productOptionId
        const productOptionId =
          rawOptionId == null || rawOptionId === 0 ? null : rawOptionId
        // Keep adds deterministic across API versions by performing one add per unit.
        for (let i = 0; i < normalizedQuantity; i += 1) {
          const result = await addItemToShoppingCart({
            shoppingCartToken,
            productId: input.productId,
            productOptionId,
            description: input.description ?? undefined,
            quantity: 1,
            unitPrice: input.unitPrice ?? undefined,
          })
          assertCartMutationSucceeded(result.addItemToShoppingCart)
        }
      }

      try {
        await addToCart(cartToken)
      } catch (error) {
        if (input.shoppingCartToken || !isRecoverableCartAddFailure(error)) {
          throw error
        }

        setShoppingCartId(null)
        const recoveredCartToken = await createCartId()
        if (!recoveredCartToken) {
          throw error
        }

        await addToCart(recoveredCartToken)
      }

      if (!opts?.skipCallbacks) {
        onSuccess?.(input)
      }
    } catch (err) {
      if (!opts?.skipCallbacks) {
        onError?.(err)
      }
      throw err
    }
  }

  return {
    addItem,
    ensureCartId,
    isPending:
      isAddingItem ||
      isAddingEbookBundle ||
      isCreatingAnon ||
      isCreatingCustomer,
    isError,
  }
}
